import { BookOpen, Building, Users, Heart, Star, Award, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import harvardImage from './images/harvard_shaktilearn.jpeg';
import whartonImage from './images/wharton_shaktilearn.jpeg';
import stanfordImage from './images/stanford_shaktilearn.jpeg';
import ucberkeleyImage from './images/berkeley_shaktilearn.jpeg';
import homepageImage from './images/homepage_image.jpeg';
import introVideo from './images/ShaktiLearn_Intro.mp4';

export default function HomeSection() {
  const navigate = useNavigate();
  const [animatedStats, setAnimatedStats] = useState({
    students: 0,
    mentors: 0,
    states: 0,
    improvement: 0
  });
  const [isVisible, setIsVisible] = useState({});
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const statsRef = useRef(null);
  const videoRef = useRef(null);

  const handleStartLearning = () => {
    navigate('/learning');
  };

  const handleBecomeMentor = () => {
    navigate('/join');
  };

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Animate stats when they come into view
  useEffect(() => {
    if (isVisible.stats) {
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      const animate = (step) => {
        if (step <= steps) {
          const progress = step / steps;
          setAnimatedStats({
            students: Math.floor(6000 * progress),
            mentors: Math.floor(20 * progress),
            states: Math.floor(3 * progress),
            improvement: Math.floor(85 * progress)
          });
          setTimeout(() => animate(step + 1), stepDuration);
        }
      };

      animate(0);
    }
  }, [isVisible.stats]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/80">
      {/* Hero Section */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 animate-bounce" style={{ animationDelay: '0s' }}>
            <Heart className="h-8 w-8 text-purple-400 opacity-60" />
          </div>
          <div className="absolute top-40 right-20 animate-bounce" style={{ animationDelay: '1s' }}>
            <Star className="h-6 w-6 text-yellow-400 opacity-60" />
          </div>
          <div className="absolute bottom-20 left-20 animate-bounce" style={{ animationDelay: '2s' }}>
            <Award className="h-7 w-7 text-purple-400 opacity-60" />
          </div>
          <div className="absolute bottom-40 right-10 animate-bounce" style={{ animationDelay: '0.5s' }}>
            <Target className="h-6 w-6 text-purple-400 opacity-60" />
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left" data-animate id="hero-text">
            <h1 className="text-5xl lg:text-6xl font-extrabold tracking-tight">
              <span className="text-gray-900 block animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                Stop Child Labour
              </span>
              <span className="text-purple-600 block animate-fade-in-up text-3xl lg:text-4xl" style={{ animationDelay: '0.4s' }}>
                Empowering Indian Women Through Education
              </span>
            </h1>
            
            <p className="mt-6 text-gray-600 text-xl max-w-3xl lg:max-w-none mx-auto lg:mx-0 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              Free personalized mentoring in English communication, digital skills, and professional development. Building pathways to economic independence.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row justify-center lg:justify-start gap-4 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
              <button 
                onClick={handleStartLearning}
                className="bg-purple-600 text-white px-8 py-3 rounded-md font-medium hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                Start Learning
              </button>
              <button 
                onClick={handleBecomeMentor}
                className="text-purple-600 px-8 py-3 rounded-md font-medium border border-purple-600 hover:bg-purple-50 transition-all duration-300 transform hover:scale-105"
              >
                Become a Mentor
              </button>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative" data-animate id="hero-image">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
              <img 
                src={homepageImage} 
                alt="Shakti Learn Students" 
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-600/20 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-transparent"></div>
            </div>
            {/* Floating stats on image */}
            <div className="absolute -top-4 -right-4 bg-white rounded-lg shadow-lg p-4 animate-fade-in-up" style={{ animationDelay: '1s' }}>
              <div className="text-2xl font-bold text-purple-600">6000+</div>
              <div className="text-xs text-gray-600">Students</div>
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-lg shadow-lg p-4 animate-fade-in-up" style={{ animationDelay: '1.2s' }}>
              <div className="text-2xl font-bold text-purple-600">20+</div>
              <div className="text-xs text-gray-600">Mentors</div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Trailer Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-animate id="video-trailer">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">Discover Shakti Learn</h2>
          <p className="text-xl text-gray-600 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Watch our story and see how we're transforming lives through education
          </p>
        </div>
        
        <div className="relative max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
            <video 
              ref={videoRef}
              className="w-full h-auto max-h-96"
              controls
              poster={homepageImage}
              preload="metadata"
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => setIsVideoPlaying(false)}
              onEnded={() => setIsVideoPlaying(false)}
            >
              <source src={introVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
          </div>
          
          {/* Floating play button overlay */}
          {!isVideoPlaying && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div 
                className="bg-white/90 backdrop-blur-sm rounded-full p-4 shadow-lg transform hover:scale-110 transition-transform duration-300 pointer-events-auto cursor-pointer"
                onClick={() => {
                  if (videoRef.current) {
                    if (videoRef.current.paused) {
                      videoRef.current.play();
                    } else {
                      videoRef.current.pause();
                    }
                  }
                }}
              >
                <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center">
                  <div className="w-0 h-0 border-l-[12px] border-l-white border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent ml-1"></div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="text-center mt-8 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <p className="text-gray-600 max-w-2xl mx-auto">
            This video showcases our mission, the students we serve, and the impact we're creating together. 
            See firsthand how personalized mentoring is changing lives across India.
          </p>
        </div>
      </div>

      {/* Impact Stats Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">The Impact We've Created Together</h2>
          <p className="text-xl text-gray-600">Every number represents a young woman's journey toward empowerment</p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12" data-animate id="stats">
          {[
            { key: 'students', number: 6000, label: "Students Empowered", suffix: "+" },
            { key: 'mentors', number: 20, label: "Dedicated Mentors", suffix: "+" },
            { key: 'states', number: 3, label: "States Reached", suffix: "" },
            { key: 'improvement', number: 85, label: "Academic Improvement", suffix: "%" }
          ].map((stat, index) => (
            <div 
              key={stat.label} 
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 p-6 text-center animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="text-4xl md:text-5xl font-bold text-purple-600">
                {animatedStats[stat.key] || 0}{stat.suffix}
              </div>
              <div className="text-gray-600 mt-2 text-sm md:text-base">{stat.label}</div>
              <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-600 h-2 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${isVisible.stats ? (animatedStats[stat.key] / stat.number) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Reach Section */}
        <div className="bg-purple-50 rounded-2xl p-8 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Our Reach</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { number: "3", label: "States" },
              { number: "15+", label: "Districts" },
              { number: "25+", label: "Villages" },
              { number: "6000+", label: "Students" }
            ].map((reach) => (
              <div key={reach.label} className="text-center">
                <div className="text-3xl font-bold text-purple-600">{reach.number}</div>
                <div className="text-gray-600 text-sm">{reach.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* With the help of */}
                  <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Powered by Our Community</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                          {[
              { number: "20+", label: "Dedicated Mentors" },
              { number: "50+", label: "Partner Schools" },
              { number: "3", label: "University Collaborations" },
              { number: "100+", label: "Supportive Community" }
            ].map((helper) => (
                <div key={helper.label} className="text-center">
                  <div className="text-3xl font-bold text-purple-600">{helper.number}</div>
                  <div className="text-gray-600 text-sm">{helper.label}</div>
                </div>
              ))}
            </div>
          </div>
      </div>

      {/* Who We Are Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-animate id="who-we-are">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-left">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Shakti Learn is a grassroots initiative dedicated to breaking down educational barriers for young Indian women. We believe that every girl deserves access to quality English education and digital literacy skills, regardless of her background or location.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Through personalized mentoring and innovative learning approaches, we're building a community where girls can develop confidence, skills, and the foundation they need to pursue their dreams and create lasting change in their communities.
            </p>
            <div className="bg-purple-50 border-l-4 border-purple-600 p-4 mb-8 rounded-r-lg">
              <p className="text-sm text-gray-700 font-medium">
                <span className="text-purple-600 font-semibold">Government Registered:</span> Shakti Learn is a government-registered trust in India, with license number INUP44022455208372X.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={handleStartLearning}
                className="bg-purple-600 text-white px-6 py-3 rounded-md font-medium hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                Start Learning
              </button>
              <button 
                onClick={handleBecomeMentor}
                className="text-purple-600 px-6 py-3 rounded-md font-medium border border-purple-600 hover:bg-purple-50 transition-all duration-300 transform hover:scale-105"
              >
                Become a Mentor
              </button>
            </div>
          </div>
          <div className="bg-purple-50 rounded-2xl p-8 animate-fade-in-right transform hover:scale-105 transition-transform duration-500">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Who We Serve</h3>
            <div className="space-y-4">
              {[
                "Come from diverse rural backgrounds",
                "Are ages 3 until high school",
                "Speak various Indian languages at home",
                "Learn English, computer skills, and confidence building"
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center space-x-3 animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <div className="w-3 h-3 bg-purple-600 rounded-full animate-pulse"></div>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Programs Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-animate id="programs">
        <h2 className="text-3xl font-bold text-gray-900 mb-4 animate-fade-in-up">Our Programs</h2>
        <p className="text-xl text-gray-600 mb-16 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          Comprehensive training designed for academic success
        </p>
        
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              title: "English Language",
              description: "One-on-one virtual tutoring focused on English communication, grammar, and vocabulary building for academic excellence.",
              icon: "📚",
              color: "from-blue-500 to-purple-600"
            },
            {
              title: "Digital Skills",
              description: "Computer literacy, software training, and basic digital skills for the modern educational landscape.",
              icon: "💻",
              color: "from-green-500 to-blue-600"
            },
            {
              title: "Academic Support",
              description: "Subject-specific tutoring, study skills, and academic guidance to improve overall performance.",
              icon: "🚀",
              color: "from-purple-500 to-pink-600"
            }
          ].map((program, index) => (
            <article 
              key={program.title} 
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 p-8 text-left animate-fade-in-up"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className={`text-4xl mb-4 animate-bounce`} style={{ animationDelay: `${index * 0.5}s` }}>
                {program.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{program.title}</h3>
              <p className="text-gray-600 mb-6">{program.description}</p>
              <div className={`w-full h-1 bg-gradient-to-r ${program.color} rounded-full transform scale-x-0 transition-transform duration-700`} 
                   style={{ transformOrigin: 'left' }}
                   onMouseEnter={(e) => e.target.style.transform = 'scaleX(1)'}
                   onMouseLeave={(e) => e.target.style.transform = 'scaleX(0)'}>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Be a Mentor Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-animate id="mentor-section">
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-12 text-center text-white relative overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-16 h-16 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>
          
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-6 animate-fade-in-up">Join Our Movement</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Become a mentor and help shape the future of young Indian women. Share your knowledge, inspire confidence, and make a real difference in someone's life. You can volunteer from anywhere in the world, teaching English and life skills.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {[
                { icon: "🏠", title: "Teach Remotely", desc: "Connect with students from anywhere in the world" },
                { icon: "⏰", title: "Flexible Schedule", desc: "Choose times that work for your lifestyle" },
                { icon: "🌟", title: "Create Change", desc: "Help girls build confidence and skills" }
              ].map((item, index) => (
                <div 
                  key={item.title}
                  className="bg-white/10 rounded-lg p-6 backdrop-blur-sm animate-fade-in-up transform hover:scale-105 transition-all duration-300"
                  style={{ animationDelay: `${0.4 + index * 0.1}s` }}
                >
                  <div className="text-3xl mb-4 animate-bounce" style={{ animationDelay: `${1 + index * 0.2}s` }}>
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-purple-100">{item.desc}</p>
                </div>
              ))}
            </div>
            <button 
              onClick={handleBecomeMentor}
              className="bg-white text-purple-600 px-8 py-3 rounded-md font-medium hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 hover:shadow-lg animate-fade-in-up"
              style={{ animationDelay: '0.8s' }}
            >
              Join Now
            </button>
          </div>
        </div>
      </div>

      {/* University Backing Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Academic Excellence</h2>
        <p className="text-xl text-gray-600 mb-12">
          Our approach is supported by educational leaders and institutions
        </p>
        
        <div className="flex flex-wrap justify-center items-center gap-12">
          <div className="flex flex-col items-center">
            <img src={harvardImage} alt="Harvard" className="h-16 object-contain grayscale hover:grayscale-0 transition-all duration-300" />
          </div>
          <div className="flex flex-col items-center">
            <img src={whartonImage} alt="Wharton" className="h-16 object-contain grayscale hover:grayscale-0 transition-all duration-300" />
          </div>
          <div className="flex flex-col items-center">
            <img src={stanfordImage} alt="Stanford" className="h-16 object-contain grayscale hover:grayscale-0 transition-all duration-300" />
          </div>
          <div className="flex flex-col items-center">
            <img src={ucberkeleyImage} alt="UC Berkeley" className="h-16 object-contain grayscale hover:grayscale-0 transition-all duration-300" />
          </div>
        </div>
      </div>
    </div>
  );
}